import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Select, 
  Switch, 
  Space, 
  Divider, 
  Typography, 
  Collapse,
  Modal,
  message,
  Tooltip,
  Radio
} from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  ArrowUpOutlined, 
  ArrowDownOutlined,
  CopyOutlined,
  ExportOutlined,
  ImportOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { FormConfig, FormFieldConfig } from '../../components/DynamicForm';
import { DynamicForm } from '../../components/DynamicForm';
import { formConfigManager } from '../../utils/formConfigManager';
import { saveFormConfig } from '../../services/api';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { TextArea } = Input;
const { Option } = Select;

// 设备类型选项
const DEVICE_TYPES = [
  { label: '塔吊', value: 0 },
  { label: '扬尘', value: 1 },
  { label: '视频', value: 3 },
  { label: '卸料平台', value: 4 },
  { label: '施工升降机', value: 5 }
];

interface FormConfigEditorProps {
  initialConfig?: FormConfig;
  onSave?: (config: FormConfig) => void;
  formType?: number; // 0: 项目表单, 1: 设备表单
}

const FIELD_TYPES = [
  { label: '文本输入框', value: 'input' },
  { label: '多行文本', value: 'textarea' },
  { label: '数字输入框', value: 'number' },
  { label: '下拉选择框', value: 'select' },
  { label: '日期选择器', value: 'date' },
  { label: '日期范围选择器', value: 'dateRange' },
  { label: '开关', value: 'switch' },
  { label: '单选框组', value: 'radio' },
  { label: '复选框组', value: 'checkbox' }
];

const FormConfigEditor: React.FC<FormConfigEditorProps> = ({ initialConfig, onSave, formType = 0 }) => {
  // 使用默认配置或初始配置
  const defaultConfig: FormConfig = {
    id: `form_${Date.now()}`,
    name: '新表单',
    description: '表单描述',
    fields: [],
    type: formType,
    device_type: undefined // 默认不设置设备类型
  };

  const [config, setConfig] = useState<FormConfig>(initialConfig || defaultConfig);
  const [form] = Form.useForm();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [jsonVisible, setJsonVisible] = useState(false);
  const [jsonValue, setJsonValue] = useState('');
  const [selectedFormType, setSelectedFormType] = useState<number>(formType);
  const [selectedDeviceType, setSelectedDeviceType] = useState<number | undefined>(
    initialConfig?.device_type
  );

  // 当初始配置变化时更新状态
  useEffect(() => {
    if (initialConfig) {
      setConfig(initialConfig);
      // 只有当初始配置中的表单类型为有效值(0或1)时才更新表单类型状态
      if (initialConfig.type === 0 || initialConfig.type === 1) {
        setSelectedFormType(initialConfig.type);
      }
      // 更新设备类型
      if (typeof initialConfig.device_type === 'number') {
        setSelectedDeviceType(initialConfig.device_type);
      }
    }
  }, [initialConfig]);

  // 当formType变化时更新状态
  useEffect(() => {
    setSelectedFormType(formType);
  }, [formType]);

  // 更新表单基本信息
  const updateFormInfo = (values: any) => {
    setConfig(prev => ({
      ...prev,
      name: values.name,
      description: values.description,
      // 不在这里更新device_type，避免与handleDeviceTypeChange冲突
    }));
  };

  // 更新表单类型
  const handleFormTypeChange = (type: number) => {
    // 只有当选择的类型为有效值(0或1)时才更新
    if (type === 0 || type === 1) {
      setSelectedFormType(type);
      // 不在这里更新config，避免多次状态更新
    }
  };

  // 更新设备类型
  const handleDeviceTypeChange = (type: number) => {
    setSelectedDeviceType(type);
    // 更新config中的device_type
    setConfig(prev => ({
      ...prev,
      device_type: type
    }));
  };

  // 添加新字段
  const addField = () => {
    const newField = formConfigManager.createField({});
    setConfig(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  // 更新字段
  const updateField = (index: number, field: Partial<FormFieldConfig>) => {
    setConfig(prev => {
      const newFields = [...prev.fields];
      newFields[index] = { ...newFields[index], ...field };
      return { ...prev, fields: newFields };
    });
  };

  // 删除字段
  const deleteField = (index: number) => {
    setConfig(prev => {
      const newFields = [...prev.fields];
      newFields.splice(index, 1);
      return { ...prev, fields: newFields };
    });
  };

  // 移动字段
  const moveField = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) || 
      (direction === 'down' && index === config.fields.length - 1)
    ) {
      return;
    }

    setConfig(prev => {
      const newFields = [...prev.fields];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      const temp = newFields[index];
      newFields[index] = newFields[targetIndex];
      newFields[targetIndex] = temp;
      return { ...prev, fields: newFields };
    });
  };

  // 复制字段
  const duplicateField = (index: number) => {
    setConfig(prev => {
      const fieldToDuplicate = { ...prev.fields[index] };
      fieldToDuplicate.key = `${fieldToDuplicate.key}_copy_${Date.now()}`;
      const newFields = [...prev.fields];
      newFields.splice(index + 1, 0, fieldToDuplicate);
      return { ...prev, fields: newFields };
    });
  };

  // 保存配置
  const saveConfig = async () => {
    if (config.fields.length === 0) {
      message.warning('表单至少需要一个字段');
      return;
    }

    // 检查字段键是否唯一
    const keys = config.fields.map(field => field.key);
    const uniqueKeys = new Set(keys);
    if (keys.length !== uniqueKeys.size) {
      message.error('字段键必须唯一');
      return;
    }

    // 创建要保存的配置对象
    const configToSave = { ...config };
    
    // 只有当选择的表单类型为有效值(0或1)时才包含在保存的配置中
    if (selectedFormType === 0 || selectedFormType === 1) {
      configToSave.type = selectedFormType;
    }
    
    // 设置设备类型
    if (typeof selectedDeviceType === 'number') {
      configToSave.device_type = selectedDeviceType;
    }
    
    if (onSave) {
      // 调用父组件的保存方法，而不是直接调用API
      onSave(configToSave);
      message.success('表单配置已提交');
    } else {
      // 如果没有提供onSave回调，则直接调用API
      try {
        const response = await saveFormConfig(configToSave);
        if (response.data.code === 0) {
          message.success('表单配置已保存');
        } else {
          message.error(response.data.msg || '保存失败');
        }
      } catch (error) {
        message.error('保存失败');
        console.error(error);
      }
    }
  };

  // 导出配置为JSON
  const exportConfig = () => {
    // 创建要导出的配置对象
    const configToExport = { ...config };
    
    // 只有当选择的表单类型为有效值(0或1)时才包含在导出的配置中
    if (selectedFormType === 0 || selectedFormType === 1) {
      configToExport.type = selectedFormType;
    }
    
    // 设置设备类型
    if (typeof selectedDeviceType === 'number') {
      configToExport.device_type = selectedDeviceType;
    }
    
    setJsonValue(JSON.stringify(configToExport, null, 2));
    setJsonVisible(true);
  };

  // 导入JSON配置
  const importConfig = () => {
    setJsonValue('');
    setJsonVisible(true);
  };

  // 处理JSON导入
  const handleJsonImport = () => {
    try {
      const importedConfig = JSON.parse(jsonValue);
      
      // 简单验证导入的配置
      if (!importedConfig.id || !importedConfig.name || !Array.isArray(importedConfig.fields)) {
        throw new Error('无效的表单配置格式');
      }
      
      setConfig(importedConfig);
      // 如果导入的配置包含表单类型，则更新表单类型
      if (typeof importedConfig.type === 'number') {
        setSelectedFormType(importedConfig.type);
      }
      // 如果导入的配置包含设备类型，则更新设备类型
      if (typeof importedConfig.device_type === 'number') {
        setSelectedDeviceType(importedConfig.device_type);
      }
      setJsonVisible(false);
      message.success('配置已导入');
    } catch (error) {
      message.error('导入失败: 无效的JSON格式');
    }
  };

  // 渲染字段选项编辑器
  const renderOptionsEditor = (field: FormFieldConfig, index: number) => {
    if (!['select', 'radio', 'checkbox'].includes(field.type)) {
      return null;
    }

    return (
      <Form.Item label="选项">
        <Form.List name={`fields.${index}.options`} initialValue={field.options || []}>
          {(fields, { add, remove }) => (
            <>
              {fields.map((optionField, optionIndex) => (
                <Space key={optionField.key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                  <Form.Item
                    {...optionField}
                    name={[optionField.name, 'label']}
                    rules={[{ required: true, message: '请输入选项标签' }]}
                    noStyle
                  >
                    <Input placeholder="选项标签" style={{ width: 120 }} />
                  </Form.Item>
                  <Form.Item
                    {...optionField}
                    name={[optionField.name, 'value']}
                    rules={[{ required: true, message: '请输入选项值' }]}
                    noStyle
                  >
                    <Input placeholder="选项值" style={{ width: 120 }} />
                  </Form.Item>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(optionField.name)}
                  />
                </Space>
              ))}
              <Button
                type="dashed"
                onClick={() => add({ label: '', value: '' })}
                icon={<PlusOutlined />}
                block
              >
                添加选项
              </Button>
            </>
          )}
        </Form.List>
      </Form.Item>
    );
  };

  // 渲染字段编辑器
  const renderFieldEditor = () => {
    return (
      <Collapse defaultActiveKey={['basic']} items={[
        {
          key: 'basic',
          label: '基本信息',
          children: (
            <Form
              layout="vertical"
              initialValues={{
                name: config.name,
                description: config.description,
                formType: selectedFormType,
                deviceType: selectedDeviceType
              }}
              onValuesChange={(_, values) => updateFormInfo(values)}
            >
              <Form.Item
                label="表单名称"
                name="name"
                rules={[{ required: true, message: '请输入表单名称' }]}
              >
                <Input placeholder="请输入表单名称" />
              </Form.Item>
              <Form.Item
                label="表单描述"
                name="description"
              >
                <TextArea placeholder="请输入表单描述" rows={2} />
              </Form.Item>
              <Form.Item label="表单类型">
                <Radio.Group 
                  value={selectedFormType} 
                  onChange={(e) => handleFormTypeChange(e.target.value)}
                  buttonStyle="solid"
                >
                  <Radio.Button value={0}>项目表单</Radio.Button>
                  <Radio.Button value={1}>设备表单</Radio.Button>
                </Radio.Group>
              </Form.Item>
              
              {/* 设备类型选择器 */}
              {selectedFormType === 1 && (
                <Form.Item 
                  label="设备类型" 
                  name="deviceType"
                  tooltip="选择设备类型"
                >
                  <Select
                    placeholder="请选择设备类型"
                    value={selectedDeviceType}
                    onChange={handleDeviceTypeChange}
                    style={{ width: '100%' }}
                  >
                    {DEVICE_TYPES.map(type => (
                      <Option key={type.value} value={type.value}>
                        {type.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
              
            </Form>
          )
        },
        {
          key: 'fields',
          label: '表单字段',
          children: (
            <>
              {config.fields.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <Text type="secondary">暂无字段，请添加字段</Text>
                </div>
              ) : (
                <Form
                  form={form}
                  layout="vertical"
                  initialValues={{ fields: config.fields }}
                >
                  {config.fields.map((field, index) => (
                    <Card
                      key={index}
                      size="small"
                      title={`字段 ${index + 1}: ${field.label || '未命名'}`}
                      style={{ marginBottom: 16 }}
                      extra={
                        <Space>
                          <Tooltip title="上移">
                            <Button
                              type="text"
                              icon={<ArrowUpOutlined />}
                              disabled={index === 0}
                              onClick={() => moveField(index, 'up')}
                            />
                          </Tooltip>
                          <Tooltip title="下移">
                            <Button
                              type="text"
                              icon={<ArrowDownOutlined />}
                              disabled={index === config.fields.length - 1}
                              onClick={() => moveField(index, 'down')}
                            />
                          </Tooltip>
                          <Tooltip title="复制">
                            <Button
                              type="text"
                              icon={<CopyOutlined />}
                              onClick={() => duplicateField(index)}
                            />
                          </Tooltip>
                          <Tooltip title="删除">
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => deleteField(index)}
                            />
                          </Tooltip>
                        </Space>
                      }
                    >
                      <Form.Item
                        label="字段标签"
                        name={['fields', index, 'label']}
                        initialValue={field.label}
                        rules={[{ required: true, message: '请输入字段标签' }]}
                      >
                        <Input
                          placeholder="请输入字段标签"
                          onChange={e => updateField(index, { label: e.target.value })}
                        />
                      </Form.Item>
                      
                      <Form.Item
                        label="字段键名"
                        name={['fields', index, 'key']}
                        initialValue={field.key}
                        rules={[{ required: true, message: '请输入字段键名' }]}
                        tooltip="字段键名用于表单数据的存储，必须唯一"
                      >
                        <Input
                          placeholder="请输入字段键名"
                          onChange={e => updateField(index, { key: e.target.value })}
                        />
                      </Form.Item>
                      
                      <Form.Item
                        label="字段类型"
                        name={['fields', index, 'type']}
                        initialValue={field.type}
                      >
                        <Select
                          placeholder="请选择字段类型"
                          onChange={value => updateField(index, { type: value })}
                        >
                          {FIELD_TYPES.map(type => (
                            <Option key={type.value} value={type.value}>
                              {type.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      
                      <Form.Item
                        label="是否必填"
                        name={['fields', index, 'required']}
                        initialValue={field.required}
                        valuePropName="checked"
                      >
                        <Switch
                          onChange={checked => updateField(index, { required: checked })}
                        />
                      </Form.Item>
                      
                      <Form.Item
                        label="占位文本"
                        name={['fields', index, 'placeholder']}
                        initialValue={field.placeholder}
                      >
                        <Input
                          placeholder="请输入占位文本"
                          onChange={e => updateField(index, { placeholder: e.target.value })}
                        />
                      </Form.Item>
                      
                      {renderOptionsEditor(field, index)}
                    </Card>
                  ))}
                </Form>
              )}
              
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addField}
                style={{ width: '100%', marginTop: 16 }}
              >
                添加字段
              </Button>
            </>
          )
        }
      ]} />
    );
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Title level={4}>表单配置编辑器</Title>
          <Space>
            <Button icon={<EyeOutlined />} onClick={() => setPreviewVisible(true)}>
              预览
            </Button>
            <Button icon={<ImportOutlined />} onClick={importConfig}>
              导入
            </Button>
            <Button icon={<ExportOutlined />} onClick={exportConfig}>
              导出
            </Button>
            <Button type="primary" onClick={saveConfig}>
              保存
            </Button>
          </Space>
        </div>
        
        <Divider />
        
        {renderFieldEditor()}
      </Card>
      
      {/* 预览模态框 */}
      <Modal
        title="表单预览"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={800}
      >
        <DynamicForm
          config={config}
          mode="create"
          onFinish={values => console.log('预览表单提交:', values)}
        />
      </Modal>
      
      {/* JSON导入/导出模态框 */}
      <Modal
        title={jsonValue ? "导出配置" : "导入配置"}
        open={jsonVisible}
        onCancel={() => setJsonVisible(false)}
        footer={jsonValue ? [
          <Button key="close" onClick={() => setJsonVisible(false)}>
            关闭
          </Button>
        ] : [
          <Button key="cancel" onClick={() => setJsonVisible(false)}>
            取消
          </Button>,
          <Button key="import" type="primary" onClick={handleJsonImport}>
            导入
          </Button>
        ]}
        width={800}
      >
        {jsonValue ? (
          <div>
            <Paragraph>
              <Text strong>配置JSON:</Text>
            </Paragraph>
            <TextArea
              value={jsonValue}
              rows={20}
              readOnly
            />
          </div>
        ) : (
          <div>
            <Paragraph>
              <Text strong>请粘贴表单配置的JSON:</Text>
            </Paragraph>
            <TextArea
              value={jsonValue}
              onChange={e => setJsonValue(e.target.value)}
              placeholder="粘贴JSON配置..."
              rows={20}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FormConfigEditor; 